// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import styled, { css, keyframes } from 'styled-components';

import { BoxSizingDefaultClassName } from '../../../../styled-components/app';
import RoutesRedirectionFactory from '../../../../utils/routes/RoutesRedirectionFactory';
import { BoomLockSmallImgPath } from '../../assets/img';
import BoomHalloweenStepCircleSvgAsComponent from '../../assets/svg/asComponent/BoomHalloweenStepCircleSvgAsComponent';
import { BoomAssetsBySkinId } from '../../constants/assets';
import BoomUIBySkinId from '../../constants/ui';
import { type BoomSkinId } from '../../types/global';
import { BoomStepStatuses, type BoomStepStatus } from '../../types/steps';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const buttonPulse = keyframes`
  0% {
    border-width: 0px;
    opacity: 1;
  }
  50% {
    border-width: 10px;
    opacity: 0;
  }
  100% {
    border-width: 0px;
    opacity: 0;
  }
`;

const StepButton = styled.div`
  position: relative;
  width: 2.7em;
  height: 2.7em;
  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $status, $skinId }) => {
    const buttonStyle = BoomUIBySkinId[$skinId]?.ButtonStyleByStatus[$status];

    return css`
      border: 2px solid ${buttonStyle.borderColor ?? 'transparent'};
    `;
  }}

  cursor: pointer;

  ${({ $pulse, $skinId }) => {
    const textStyle = BoomUIBySkinId[$skinId]?.TEXT_STYLE;

    return $pulse
      ? css`
          &:before {
            content: '';
            position: absolute;
            border: 8px solid ${textStyle.pendingColor};
            width: 2.7em;
            height: 2.7em;
            border-radius: 50%;
            box-sizing: content-box;
            z-index: -1;
            animation: ${buttonPulse} 2s cubic-bezier(0, 0, 0.5, 1) infinite;
          }
        `
      : undefined;
  }}
`;

const ButtonContent = styled.div`
  margin-top: 0.3em;
  font-size: 1.1em;
  width: 1.3em;
  text-align: center;
  border-radius: 50%;

  ${({ $status, $skinId }) => {
    const buttonStyle = BoomUIBySkinId[$skinId]?.ButtonStyleByStatus[$status];

    return css`
      color: ${buttonStyle.color ?? 'white'};
      background-color: ${buttonStyle.backgroundColor ?? 'transparent'};
    `;
  }}
`;

const StyledStepCircle = styled(BoomHalloweenStepCircleSvgAsComponent)`
  height: 100%;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $isLarge }) =>
    !$isLarge
      ? css`
          padding: 0.1em;
          font-size: 2em;
        `
      : css`
          height: 2.7em;
          width: 2.7em;

          svg {
            height: 2.7em;
            width: 2.7em;
          }
        `}
`;

const ButtonImgWrapper = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  font-size: 2em;
  padding: 0.25em;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonImg = styled.img`
  height: 100%;
`;

type PropsType = {
  className?: string;
  stepId: string;
  step: number;
  status: BoomStepStatus;
  skinId: BoomSkinId;
};

const BoomStepItem = ({ className = '', stepId, step, status, skinId }: PropsType) => {
  const history = useHistory();

  const onClick = useCallback(() => {
    history.push(RoutesRedirectionFactory.getBoomStepRouteConfig(stepId));
  }, [history, stepId]);

  const buttonImgSrc = useMemo(() => {
    if (status === BoomStepStatuses.LOCKED) {
      return BoomLockSmallImgPath;
    }
    if (status === BoomStepStatuses.OPEN || status === BoomStepStatuses.PENDING) {
      return BoomAssetsBySkinId[skinId].SmallStepIconsByStepId[stepId];
    }
    return null;
  }, [status, skinId, stepId]);

  return (
    <Wrapper className={className}>
      <StepButton
        $status={status}
        onClick={onClick}
        $pulse={status === BoomStepStatuses.PENDING}
        $skinId={skinId}
        className={BoxSizingDefaultClassName}
      >
        <StyledStepCircle skinId={skinId} status={status} $isLarge={status === BoomStepStatuses.LOCKED} />
        {buttonImgSrc ? (
          <ButtonImgWrapper>
            <ButtonImg src={buttonImgSrc} />
          </ButtonImgWrapper>
        ) : null}
      </StepButton>
      <ButtonContent $status={status} $skinId={skinId}>
        {step}
      </ButtonContent>
    </Wrapper>
  );
};

export default BoomStepItem;
